// Role-Based Access Control (RBAC) Service for ZeroPath
// Based on user stories requirements - 5 roles with granular permissions

import { db } from '../firebase';
import { filterAssignableRoles, isSuperadminRole, SUPERADMIN_ROLE_ID } from '../constants/rbacConstants';

export { SUPERADMIN_ROLE_ID, isSuperadminRole, filterAssignableRoles };

// SECURITY: This class must have exactly one initialize() and one getRole() (sync cache) + getRoleById() (async).
// Duplicate method names cause the second definition to overwrite the first; permission checks would then be wrong.
class RBACService {
  constructor() {
    // Cache for roles and permissions
    this.rolesCache = null;
    this.permissionsCache = null;
    this.rolePermissionsCache = null;
    this.initialized = false;
    
    // Define system permissions based on user stories
    this.permissions = {
      // System Configuration
      'system:config': 'System configuration and settings',
      
      // User Management
      'users:manage': 'User management and role assignment',
      
      // Protocol Management
      'protocols:define': 'Define infection prevention protocols',
      'protocols:review': 'Review and approve protocols',
      'protocols:input': 'Input protocol data for patients',
      
      // RPN Configuration
      'rpn:config': 'Configure RPN parameters',
      'rpn:adjust': 'Adjust RPN parameters',
      'rpn:patient': 'Set patient-specific RPN factors',
      
      // Task Management
      'tasks:create_dept': 'Create department-level tasks',
      'tasks:create_room': 'Create room-level tasks',
      'tasks:create_patient': 'Create patient-specific tasks',
      'tasks:assign': 'Assign tasks to personnel',
      'tasks:execute': 'Execute assigned tasks',
      
      // Compliance Monitoring
      'compliance:monitor_full': 'Full compliance monitoring access',
      'compliance:monitor_dept': 'Department-level compliance monitoring',
      'compliance:monitor_patient': 'Patient-level compliance monitoring',
      
      // Analytics and Reporting
      'analytics:all': 'Access to all analytics',
      'analytics:compliance': 'Compliance analytics',
      'analytics:dept': 'Department analytics',
      'analytics:patient': 'Patient analytics',
      
      // Audit and Investigation
      'audit:full': 'Full audit trail access',
      'audit:investigate': 'Investigation access',
      'audit:dept': 'Department audit access',
      'audit:patient': 'Patient audit access',
      
      // QMS Audit Lifecycle (PRD: Audit_Lifecycle_v2)
      'audit:manage': 'Manage QMS audits (create, edit, complete, close)',
      'audit:view': 'View QMS audits',
      
      // Education Management
      'education:manage': 'Manage educational content',
      
      // Workflow Management (Phase 5)
      'workflows:view': 'View workflow definitions and executions',
      'workflows:manage': 'Manage workflow definitions and templates',
      'workflows:execute': 'Start and manage workflow executions',
      'workflows:monitor': 'Monitor workflow executions and statistics',
      
      // RFID Access
      'rfid:access': 'RFID/NFC badge access',

      // Clinical structure (buildings, floors, beds, organizations) – also enforced in Firestore via canManageClinicalStructure()
      'structure:manage': 'Manage clinical structure (buildings, floors, beds, organizations)'
    };

    // Define the 5 roles from user stories with their permissions
    this.roles = {
      'management': {
        name: 'Management',
        description: 'Full system access and control',
        permissions: [
          'system:config', 'users:manage', 'protocols:define', 'rpn:config',
          'tasks:create_dept', 'tasks:create_room', 'tasks:create_patient', 'tasks:assign',
          'compliance:monitor_full', 'analytics:all', 'audit:full',
          'audit:manage', 'audit:view',
          'education:manage', 'rfid:access', 'structure:manage'
        ],
        level: 100
      },
      'ic_personnel': {
        name: 'IC Personnel',
        description: 'Infection Control specialists with review/approve permissions',
        permissions: [
          'protocols:define', 'protocols:review', 'rpn:config', 'rpn:adjust',
          'tasks:create_dept', 'tasks:create_room', 'tasks:create_patient', 'tasks:assign',
          'compliance:monitor_full', 'analytics:compliance', 'audit:investigate',
          'audit:manage', 'audit:view',
          'education:create', 'education:view', 'rfid:access', 'structure:manage'
        ],
        level: 90
      },
      'administration': {
        name: 'Administration',
        description: 'Department administration with limited access',
        permissions: [
          'tasks:create_dept', 'tasks:create_room', 'tasks:assign',
          'compliance:monitor_dept', 'analytics:dept', 'audit:dept',
          'education:view', 'rfid:access', 'structure:manage'
        ],
        level: 70
      },
      'execution': {
        name: 'Execution',
        description: 'Task execution staff with RFID access',
        permissions: [
          'tasks:execute', 'education:view', 'rfid:access'
        ],
        level: 30
      },
      'prescriptors': {
        name: 'Prescriptors',
        description: 'Medical staff with patient-specific permissions',
        permissions: [
          'protocols:input', 'rpn:patient', 'tasks:create_patient',
          'compliance:monitor_patient', 'analytics:patient', 'audit:patient',
          'education:view', 'rfid:access'
        ],
        level: 50
      }
    };

    // Current user context (will be set when user logs in)
    this.currentUser = null;
    this.currentUserRole = null;
  }

  // Set current user context
  setCurrentUser(user, role) {
    // This method is kept for compatibility but should not be used
    console.warn('setCurrentUser is deprecated. Pass userRole as parameter to methods instead.');
  }

  // Check if user has a specific permission
  async hasPermission(permission, userRole) {
    if (!userRole) return false;
    
    try {
      // Load role permissions from database if not cached
      if (!this.rolePermissionsCache) {
        await this.loadRolePermissions();
      }
      
      // Check if role has permission
      const rolePermissions = this.rolePermissionsCache[userRole] || [];
      return rolePermissions.includes(permission);
    } catch (error) {
      console.error('Error checking permission:', error);
      // Fallback to hardcoded permissions
      const role = this.roles[userRole];
      return role && role.permissions.includes(permission);
    }
  }

  // Check if current user has any of the specified permissions
  async hasAnyPermission(permissions) {
    for (const permission of permissions) {
      if (await this.hasPermission(permission)) {
        return true;
      }
    }
    return false;
  }

  // Check if current user has all of the specified permissions
  async hasAllPermissions(permissions) {
    for (const permission of permissions) {
      if (!(await this.hasPermission(permission))) {
        return false;
      }
    }
    return true;
  }

  // Check if current user's role level is sufficient
  hasRoleLevel(requiredLevel) {
    if (!this.currentUserRole) return false;
    
    const role = this.rolesCache[this.currentUserRole];
    return role && role.level >= requiredLevel;
  }

  // Get all permissions for current user
  getUserPermissions() {
    if (!this.currentUserRole) return [];
    
    const role = this.rolesCache[this.currentUserRole];
    return role ? role.permissions : [];
  }

  // Get role information
  getRole(roleKey) {
    if (!roleKey || !this.rolesCache) {
      return null;
    }
    return this.rolesCache[roleKey] || null;
  }

  // Load roles from database
  async loadRoles() {
    try {
      const { collection, getDocs } = await import('firebase/firestore');
      const rolesSnapshot = await getDocs(collection(db, 'roles'));
      const roles = {};
      rolesSnapshot.forEach(doc => {
        roles[doc.id] = { ...doc.data(), id: doc.id };
      });
      // Ensure roles that can manage clinical structure (per Firestore canManageClinicalStructure) have structure:manage in matrix
      const structureManageRoleIds = ['management', 'administration', 'ic_personnel', 'admin'];
      structureManageRoleIds.forEach(roleKey => {
        if (roles[roleKey] && !(roles[roleKey].permissions || []).includes('structure:manage')) {
          roles[roleKey].permissions = [...(roles[roleKey].permissions || []), 'structure:manage'];
        }
      });
      this.rolesCache = roles;
      console.log('✅ Roles loaded from database:', Object.keys(roles));
      return roles;
    } catch (error) {
      console.error('❌ Error loading roles from database:', error);
      throw new Error('Failed to load roles from database. Check Firebase connection.');
    }
  }

  // Load permissions from database (merge with code-defined permissions so matrix shows all, including structure:manage)
  async loadPermissions() {
    try {
      const codePerms = {};
      Object.entries(this.permissions).forEach(([k, v]) => {
        codePerms[k] = typeof v === 'string' ? { description: v } : v;
      });
      const { collection, getDocs } = await import('firebase/firestore');
      const permissionsSnapshot = await getDocs(collection(db, 'permissions'));
      const fromDb = {};
      permissionsSnapshot.forEach(doc => {
        fromDb[doc.id] = doc.data();
      });
      this.permissionsCache = { ...codePerms, ...fromDb };
      console.log('✅ Permissions loaded from database:', Object.keys(this.permissionsCache));
      return this.permissionsCache;
    } catch (error) {
      console.error('❌ Error loading permissions from database:', error);
      this.permissionsCache = Object.fromEntries(
        Object.entries(this.permissions).map(([k, v]) => [k, typeof v === 'string' ? { description: v } : v])
      );
      return this.permissionsCache;
    }
  }

  // Load role-permission mappings from database
  async loadRolePermissions() {
    try {
      // Since roles already contain permissions in the database, we don't need a separate mapping
      // Just extract permissions from each role
      const rolePermissions = {};
      Object.keys(this.rolesCache).forEach(roleKey => {
        rolePermissions[roleKey] = this.rolesCache[roleKey].permissions || [];
      });
      this.rolePermissionsCache = rolePermissions;
      console.log('✅ Role-permissions loaded from database');
      return rolePermissions;
    } catch (error) {
      console.error('❌ Error loading role-permissions from database:', error);
      throw new Error('Failed to load role-permissions from database. Check Firebase connection.');
    }
  }

  // Initialize RBAC service: load from DB, fallback to hardcoded roles on failure
  async initialize() {
    if (this.initialized) return;
    
    try {
      await this.loadRoles();
      await Promise.all([
        this.loadPermissions(),
        this.loadRolePermissions()
      ]);
      this.initialized = true;
      console.log('✅ RBAC Service initialized with database data');
    } catch (error) {
      console.error('❌ Failed to initialize RBAC service, using hardcoded roles:', error);
      this.rolesCache = this.roles;
      this.initialized = true;
    }
  }

  // Get all roles (synchronous - returns cached data)
  getAllRoles() {
    return this.rolesCache || {};
  }

  /** Roles that may be assigned to hospital users (excludes platform superadmin). */
  getAssignableRoles() {
    return filterAssignableRoles(this.getAllRoles());
  }

  // Get all available permissions (synchronous - returns cached data)
  getAllPermissions() {
    return this.permissionsCache || {};
  }

  // Fetch role by id (cache first, then database)
  async getRoleById(roleId) {
    try {
      if (this.rolesCache && this.rolesCache[roleId]) {
        return this.rolesCache[roleId];
      }
      
      const { doc, getDoc } = await import('firebase/firestore');
      const roleDoc = await getDoc(doc(db, 'roles', roleId));
      
      if (!roleDoc.exists()) {
        throw new Error(`Role not found: ${roleId}`);
      }
      
      return roleDoc.data();
    } catch (error) {
      console.error('Error fetching role:', error);
      throw error;
    }
  }
  
  // Get user's dashboard configuration (production method)
  async getUserDashboard(userRole) {
    try {
      const role = await this.getRoleById(userRole);
      
      // If role exists but missing dashboard field, use fallback mapping
      if (role && !role.dashboard) {
        console.warn(`Role ${userRole} missing dashboard field, using fallback mapping`);
        const dashboardMapping = {
          'management': 'ManagementDashboard',
          'ic_personnel': 'ICPersonnelDashboard',
          'administration': 'AdministrationDashboard',
          'execution': 'ExecutionDashboard',
          'prescriptors': 'PrescriptorsDashboard'
        };
        
        return {
          dashboard: dashboardMapping[userRole] || 'ExecutionDashboard',
          permissions: role.permissions || this.roles[userRole]?.permissions || [],
          level: role.level || this.roles[userRole]?.level || 0,
          name: role.name || this.roles[userRole]?.name || userRole,
          description: role.description || this.roles[userRole]?.description || ''
        };
      }
      
      return {
        dashboard: role.dashboard,
        permissions: role.permissions || [],
        level: role.level,
        name: role.name,
        description: role.description
      };
    } catch (error) {
      console.error('Error getting dashboard config:', error);
      // Fallback to hardcoded roles during transition
      if (this.roles[userRole]) {
        console.warn(`Using hardcoded fallback for role ${userRole}`);
        const dashboardMapping = {
          'management': 'ManagementDashboard',
          'ic_personnel': 'ICPersonnelDashboard',
          'administration': 'AdministrationDashboard',
          'execution': 'ExecutionDashboard',
          'prescriptors': 'PrescriptorsDashboard'
        };
        
        return {
          dashboard: dashboardMapping[userRole] || 'ExecutionDashboard',
          permissions: this.roles[userRole].permissions || [],
          level: this.roles[userRole].level || 0,
          name: this.roles[userRole].name || userRole,
          description: this.roles[userRole].description || ''
        };
      }
      throw error;
    }
  }
  
  // Validate user has valid role (production method)
  async validateUserRole(userRole) {
    try {
      const role = await this.getRoleById(userRole);
      
      // During transition: if role exists but no dashboard field, use fallback
      if (role && !role.dashboard) {
        console.warn(`Role ${userRole} exists but missing dashboard field - using fallback`);
        return true; // Allow login with fallback dashboard
      }
      
      return !!role && !!role.dashboard;
    } catch (error) {
      console.error('Role validation failed:', error);
      // During transition: if role exists in hardcoded roles, allow it
      if (this.roles[userRole]) {
        console.warn(`Role ${userRole} not in database, using hardcoded fallback`);
        return true;
      }
      return false;
    }
  }


  // Check if user can access a specific resource
  async canAccess(resource, action) {
    const permission = `${resource}:${action}`;
    return await this.hasPermission(permission);
  }

  // Check if user can manage another user (based on role levels)
  canManageUser(targetUserRole) {
    if (!this.currentUserRole) return false;
    
    const currentRole = this.roles[this.currentUserRole];
    const targetRole = this.roles[targetUserRole];
    
    if (!currentRole || !targetRole) return false;
    
    // Can manage users with lower role levels
    return currentRole.level > targetRole.level;
  }

  // Get accessible menu items based on permissions
  async getAccessibleMenuItems() {
    const menuItems = [
      { key: 'clinical-structure', label: 'Clinical Structure', permission: 'clinical:view' },
      { key: 'user-admin', label: 'User Administration', permission: 'users:view' },
      { key: 'ipc-setup', label: 'IPC Setup', permission: 'ipc:view' },
      { key: 'biological-entities', label: 'HAI Biological Entities', permission: 'biological:view' },
      { key: 'reports', label: 'Reports & Analytics', permission: 'reports:view' },
      { key: 'tasks', label: 'Task Management', permission: 'tasks:view' },
      { key: 'emergency', label: 'Emergency Protocols', permission: 'emergency:view' }
    ];

    // Note: This method now returns a promise due to async hasPermission
    // Callers should await this method
    const filteredItems = [];
    for (const item of menuItems) {
      if (await this.hasPermission(item.permission)) {
        filteredItems.push(item);
      }
    }
    return filteredItems;
  }

  // Working permission management methods
  updateRolePermissions(roleKey, permissions) {
    if (this.roles[roleKey]) {
      this.roles[roleKey].permissions = permissions;
      console.log(`✅ Updated permissions for role: ${roleKey}`);
      return true;
    }
    return false;
  }

  addPermissionToRole(roleKey, permission) {
    if (this.rolesCache[roleKey] && !this.rolesCache[roleKey].permissions.includes(permission)) {
      this.rolesCache[roleKey].permissions.push(permission);
      console.log(`✅ Added permission ${permission} to role: ${roleKey}`);
      return true;
    }
    return false;
  }

  removePermissionFromRole(roleKey, permission) {
    if (this.rolesCache[roleKey]) {
      const index = this.rolesCache[roleKey].permissions.indexOf(permission);
      if (index > -1) {
        this.rolesCache[roleKey].permissions.splice(index, 1);
        console.log(`✅ Removed permission ${permission} from role: ${roleKey}`);
        return true;
      }
    }
    return false;
  }

  createCustomRole(roleKey, name, description, permissions, level) {
    this.rolesCache[roleKey] = {
      name,
      description,
      permissions,
      level
    };
    console.log(`✅ Created custom role: ${roleKey}`);
    return true;
  }

  deleteRole(roleKey) {
    if (this.rolesCache[roleKey] && !['management', 'ic_personnel', 'administration', 'execution', 'prescriptors'].includes(roleKey)) {
      delete this.rolesCache[roleKey];
      console.log(`✅ Deleted role: ${roleKey}`);
      return true;
    }
    return false;
  }

  // Get permission matrix for visualization
  getPermissionMatrix() {
    const matrix = {};
    Object.keys(this.rolesCache || {}).forEach(roleKey => {
      matrix[roleKey] = {};
      Object.keys(this.permissionsCache || {}).forEach(permission => {
        matrix[roleKey][permission] = (this.rolesCache[roleKey].permissions || []).includes(permission);
      });
    });
    return matrix;
  }

  // Validate permission configuration
  validatePermissions() {
    const issues = [];
    
    // Check if all roles have at least one permission
    Object.entries(this.roles).forEach(([roleKey, role]) => {
      if (role.permissions.length === 0) {
        issues.push(`Role ${roleKey} has no permissions`);
      }
    });
    
    // Check for orphaned permissions
    const allRolePermissions = new Set();
    Object.values(this.roles).forEach(role => {
      role.permissions.forEach(permission => allRolePermissions.add(permission));
    });
    
    Object.keys(this.permissions).forEach(permission => {
      if (!allRolePermissions.has(permission)) {
        issues.push(`Permission ${permission} is not assigned to any role`);
      }
    });
    
    return issues;
  }

  // Audit log for permission checks
  logPermissionCheck(permission, granted, context = {}) {
    console.log(`🔐 RBAC Check: ${permission} - ${granted ? 'GRANTED' : 'DENIED'}`, {
      user: this.currentUser?.id,
      role: this.currentUserRole,
      context
    });
  }
}

// Create singleton instance
const rbacService = new RBACService();

export default rbacService;
